<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { Palette, Sun, Moon, ChevronDown, Check } from 'lucide-vue-next';
import { useTheme } from '../../composables/useTheme';
import { cn } from '../../design-system/utils';

const { t } = useI18n();
const {
  currentTheme,
  currentMode,
  availableThemes,
  setTheme,
  setMode,
} = useTheme();

// Mapping nom du theme -> clés i18n (label + vibe)
const themeI18nKeys: Record<string, { label: string; vibe: string }> = {
  atelier:  { label: 'common.theme.atelier_label',  vibe: 'common.theme.atelier_vibe' },
  premium:  { label: 'common.theme.premium_label',  vibe: 'common.theme.premium_vibe' },
  chantier: { label: 'common.theme.chantier_label', vibe: 'common.theme.chantier_vibe' },
};

const open = ref(false);
const triggerEl = ref<HTMLElement | null>(null);
const panelTop = ref(0);
const panelRight = ref(0);

const isDark = computed(() => currentMode.value === 'dark');

function updatePanelPosition() {
  if (!triggerEl.value) return;
  const rect = triggerEl.value.getBoundingClientRect();
  panelTop.value = rect.bottom + 8;
  panelRight.value = Math.max(window.innerWidth - rect.right, 16);
}

async function toggleOpen() {
  open.value = !open.value;
  if (open.value) {
    await nextTick();
    updatePanelPosition();
  }
}

function close() {
  open.value = false;
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) close();
}

function onResize() {
  if (open.value) updatePanelPosition();
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
  window.addEventListener('resize', onResize);
  window.addEventListener('scroll', onResize, { passive: true });
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  window.removeEventListener('resize', onResize);
  window.removeEventListener('scroll', onResize);
});
</script>

<template>
  <div class="relative">
    <!-- Trigger button inline header -->
    <button
      ref="triggerEl"
      type="button"
      :class="cn(
        'inline-flex items-center gap-2 h-8 px-3 rounded-md text-sm font-medium',
        'transition-colors duration-fast ease-standard',
        'focus-visible:outline-none focus-visible:shadow-focus',
        open ? 'bg-bg-muted text-text' : 'text-text hover:bg-bg-muted',
      )"
      :aria-expanded="open"
      aria-haspopup="menu"
      :aria-label="t('common.theme.select_theme_aria')"
      @click="toggleOpen"
    >
      <Palette :size="14" aria-hidden="true" />
      <span class="hidden sm:inline">{{ t('common.nav.appearance') }}</span>
      <ChevronDown
        :size="12"
        aria-hidden="true"
        :class="cn('transition-transform duration-fast', open && 'rotate-180')"
      />
    </button>

    <!-- Overlay + Panel teleportés à body pour echapper au stacking context du header sticky -->
    <Teleport to="body">
      <!-- Overlay backdrop-blur derriere le panel -->
      <Transition
        enter-active-class="transition-opacity duration ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-fast"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="open"
          class="fixed inset-0 z-overlay bg-overlay backdrop-blur-sm"
          aria-hidden="true"
          @click="close"
        />
      </Transition>

      <!-- Panel — fixed position calculee depuis le trigger, z-modal > z-overlay -->
      <Transition
        enter-active-class="transition-all duration-fast ease-emphasized"
        enter-from-class="opacity-0 scale-95 -translate-y-1"
        enter-to-class="opacity-100 scale-100 translate-y-0"
        leave-active-class="transition-all duration-fast ease-standard"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95 -translate-y-1"
      >
        <div
          v-if="open"
          class="fixed w-80 bg-card border border-border rounded-xl shadow-xl overflow-hidden z-modal"
          :style="{ top: `${panelTop}px`, right: `${panelRight}px` }"
          role="menu"
        >
          <!-- Section Theme -->
          <section class="px-4 py-4 flex flex-col gap-3">
            <span class="text-xs font-medium uppercase tracking-wide text-text-muted">{{ t('common.theme.section_title') }}</span>
            <div class="flex flex-col gap-2">
              <button
                v-for="theme in availableThemes"
                :key="theme.name"
                type="button"
                role="menuitemradio"
                :aria-checked="currentTheme === theme.name"
                :class="cn(
                  'flex items-center gap-3 p-2.5 rounded-lg border text-left transition-colors duration-fast',
                  'focus-visible:outline-none focus-visible:shadow-focus',
                  currentTheme === theme.name
                    ? 'border-accent bg-bg-muted'
                    : 'border-border hover:border-border-strong hover:bg-bg-muted',
                )"
                @click="setTheme(theme.name)"
              >
                <span class="flex shrink-0 items-center gap-px rounded-md overflow-hidden border border-border-subtle" aria-hidden="true">
                  <span class="h-7 w-3" :style="{ backgroundColor: theme.preview.background }" />
                  <span class="h-7 w-3" :style="{ backgroundColor: theme.preview.surface }" />
                  <span class="h-7 w-3" :style="{ backgroundColor: theme.preview.accent }" />
                </span>
                <span class="flex flex-col gap-0.5 flex-1 min-w-0">
                  <span class="text-sm font-semibold text-text">{{ t(themeI18nKeys[theme.name].label) }}</span>
                  <span class="text-xs text-text-muted leading-snug">{{ t(themeI18nKeys[theme.name].vibe) }}</span>
                </span>
                <Check
                  :size="14"
                  :class="cn(
                    'shrink-0 transition-opacity duration-fast',
                    currentTheme === theme.name ? 'text-accent opacity-100' : 'opacity-0',
                  )"
                  aria-hidden="true"
                />
              </button>
            </div>
          </section>

          <!-- Section Mode -->
          <section class="px-4 py-4 border-t border-border flex flex-col gap-3">
            <span class="text-xs font-medium uppercase tracking-wide text-text-muted">{{ t('common.theme.mode_section_title') }}</span>
            <div class="flex p-1 rounded-lg bg-bg-muted border border-border-subtle" role="group" :aria-label="t('common.theme.mode_section_title')">
              <button
                type="button"
                role="menuitemradio"
                :aria-checked="!isDark"
                :class="cn(
                  'flex-1 h-9 rounded-md flex items-center justify-center gap-2 text-sm font-medium transition-colors duration-fast',
                  'focus-visible:outline-none focus-visible:shadow-focus',
                  !isDark ? 'bg-card text-text shadow-sm' : 'text-text-muted hover:text-text-secondary',
                )"
                @click="setMode('light')"
              >
                <Sun :size="14" aria-hidden="true" />
                {{ t('common.theme.light') }}
              </button>
              <button
                type="button"
                role="menuitemradio"
                :aria-checked="isDark"
                :class="cn(
                  'flex-1 h-9 rounded-md flex items-center justify-center gap-2 text-sm font-medium transition-colors duration-fast',
                  'focus-visible:outline-none focus-visible:shadow-focus',
                  isDark ? 'bg-card text-text shadow-sm' : 'text-text-muted hover:text-text-secondary',
                )"
                @click="setMode('dark')"
              >
                <Moon :size="14" aria-hidden="true" />
                {{ t('common.theme.dark') }}
              </button>
            </div>
          </section>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>
